import DateHelper from '../../Utility/DateHelper';
import { isIOS } from 'mobile-device-detect';
import { IPlayerState } from './IPlayerState';
import { setLives } from './PlayerInventoryHelper';
import { getCompletedLevels } from './PlayerLevelHelper';
import {
    updateKeyMissionTimer,
    updateMissionTimer,
} from './PlayerMissionHelper';
import { unlockNextWorld } from './PlayerStateHelper';
import { setWheelSpins } from './PlayerWheelHelper';

export function updateLifeTimer(state: IPlayerState): IPlayerState {
    // If player has 5 or more lives stop the life-timer.
    if (state.lives >= 5) {
        return {
            ...state,
            lifeTimer: null,
        };
    }

    // The player has less than five lives and the timer is not set - start the timer.
    if (state.lifeTimer === null) {
        return {
            ...state,
            lifeTimer: DateHelper.now().toString(),
        };
    }

    // Check if the player has earned lives since the timer has started.
    // This may include multiple lives (up to a max of 5) if the player has not collected
    // their lives over the specified interval (30 minutes).
    // The timer is then set back to account for the received lives but still retains duration
    // towards the player's next life if still less than 5 lives.

    const timeDiff = DateHelper.getElapsedTime(state.lifeTimer);
    const thirtyMinutes = DateHelper.minutesToMilliseconds(30);

    if (timeDiff >= thirtyMinutes) {
        const livesEarned = Math.floor(timeDiff / thirtyMinutes);
        const addLives = Math.min(5 - state.lives, livesEarned);

        const remainingDuration = timeDiff % thirtyMinutes;

        state = {
            ...state,
            lifeTimer: DateHelper.getDateFromValue(
                DateHelper.now().getTime() - remainingDuration
            ).toString(),
        };

        state = setLives(state, state.lives + addLives);
        return state;
    }

    // No change.
    return state;
}

export function updateGateTimer(state: IPlayerState): IPlayerState {
    // Find the player's current max level.
    const maxLevel = getCompletedLevels(state.levels.saga).reduce(
        (a, b) => (a = b.levelNumber > a ? b.levelNumber : a),
        0
    );

    // No timer set.
    // If player is currently on or beyond the last level of their current world start the timer.
    if (state.gateTimer === null && maxLevel >= state.world * 60) {
        return {
            ...state,
            gateTimer: DateHelper.now().toString(),
        };
    }

    // No timer set - must not be at last level yet.
    if (state.gateTimer === null) return state;

    // Check if the level is unlocked.
    // One day has elapsed. Reset gate timer and increment player world.
    if (
        DateHelper.isTimeUp(state.gateTimer, DateHelper.daysToMilliseconds(1))
    ) {
        return unlockNextWorld(state);
    }

    return state;
}

export function updateWheelTimer(state: IPlayerState): IPlayerState {
    // If player has spins, stop the wheel-timer.
    if (state.wheelSpins > 0) {
        return {
            ...state,
            wheelTimer: null,
        };
    }

    // If the player has no spins and the timer is not set - start it.
    if (state.wheelTimer === null) {
        return {
            ...state,
            wheelTimer: DateHelper.now().toString(),
        };
    }

    // Check if the player has earned spins since the timer has started.
    if (
        DateHelper.isTimeUp(
            state.wheelTimer,
            DateHelper.hoursToMilliseconds(12)
        )
    ) {
        return setWheelSpins(state, 1);
    }

    // No change.
    return state;
}

export function updateFoxTimer(state: IPlayerState): IPlayerState {
    if (state.foxTimer === null) {
        return {
            ...state,
            foxTimer: DateHelper.now().toString(),
        };
    }

    // No change.
    return state;
}

export function updateLifeSharePrizeTimer(
    playerState: IPlayerState
): IPlayerState {
    // No timer set.
    if (playerState.lifeSharePrizeTimer === null) {
        return playerState;
    }

    // Check if time is up.
    // One day has elapsed - remove the prize timer.
    if (
        DateHelper.isTimeUp(
            playerState.lifeSharePrizeTimer,
            DateHelper.daysToMilliseconds(1)
        )
    ) {
        return {
            ...playerState,
            lifeSharePrizeTimer: null,
        };
    }

    // No change.
    return playerState;
}

export function updateFriendTimers(playerState: IPlayerState): IPlayerState {
    const threeDays = DateHelper.daysToMilliseconds(3);
    const cooldown = isIOS ? threeDays : DateHelper.hoursToMilliseconds(12);

    const friendsData = playerState.friendData.map(friendData => {
        if (
            friendData.lifeSendTimer !== null &&
            DateHelper.isTimeUp(friendData.lifeSendTimer, threeDays)
        ) {
            friendData = {
                ...friendData,
                lifeSendTimer: null,
            };
        }

        if (
            friendData.lifeRequestTimer !== null &&
            DateHelper.isTimeUp(friendData.lifeRequestTimer, threeDays)
        ) {
            friendData = {
                ...friendData,
                lifeRequestTimer: null,
            };
        }

        if (
            friendData.giftTimer !== null &&
            DateHelper.isTimeUp(friendData.giftTimer, cooldown)
        ) {
            friendData = {
                ...friendData,
                giftTimer: null,
            };
        }

        return friendData;
    });

    return {
        ...playerState,
        friendData: friendsData,
    };
}

export function updateTimers(state: IPlayerState): IPlayerState {
    state = updateLifeTimer(state);
    state = updateGateTimer(state);
    state = updateLifeSharePrizeTimer(state);
    state = updateWheelTimer(state);
    state = updateFoxTimer(state);
    state = updateFriendTimers(state);

    state = updateMissionTimer(state);
    state = updateKeyMissionTimer(state);

    return state;
}
